import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { Suspense, lazy, useEffect } from 'react'
import { BrandLeaf } from '@angan-greens/ui'
import useStore from './store/useStore'
import { trackEvent } from './lib/firebase'

function PageViewTracker() {
  const location = useLocation()
  useEffect(() => {
    trackEvent('page_view', {
      page_path:     location.pathname + location.search,
      page_location: window.location.href,
      page_title:    document.title,
    })
  }, [location.pathname, location.search])
  return null
}

const LoginPage     = lazy(() => import('./pages/auth/LoginPage'))
const Shell         = lazy(() => import('./components/layout/Shell'))
const DashboardPage = lazy(() => import('./pages/dashboard/DashboardPage'))
const InventoryPage = lazy(() => import('./pages/inventory/InventoryPage'))
const OrdersPage    = lazy(() => import('./pages/orders/OrdersPage'))
const PackingSlip   = lazy(() => import('./pages/orders/PackingSlip'))
const ServiceRequestsPage = lazy(() => import('./pages/serviceRequests/ServiceRequestsPage'))
const PayoutsPage        = lazy(() => import('./pages/payouts/PayoutsPage'))
const PayoutReceiptPage  = lazy(() => import('./pages/payouts/PayoutReceiptPage'))
const TaxInvoicePage     = lazy(() => import('./pages/payouts/TaxInvoicePage'))
const AgreementsPage     = lazy(() => import('./pages/agreements/AgreementsPage'))
const AccountPage    = lazy(() => import('./pages/account/AccountPage'))
const NotFound      = lazy(() => import('./pages/NotFound'))

function Loader() {
  return (
    <div className="min-h-screen bg-off-white flex items-center justify-center">
      <BrandLeaf size={40} className="animate-pulse" />
    </div>
  )
}

function RequireSupplier({ children }: { children: React.ReactNode }) {
  const user      = useStore(s => s.user)
  const isLoading = useStore(s => s.isLoading)
  if (isLoading) return <Loader />
  if (!user)                   return <Navigate to="/login"   replace />
  if (user.role !== 'supplier') return <Navigate to="/login"   replace />
  return <>{children}</>
}

export default function App() {
  return (
    <BrowserRouter>
      <PageViewTracker />
      <Suspense fallback={<Loader />}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          {/* Packing-slip view sits OUTSIDE the Shell on purpose — the
               supplier opens this in a new tab and prints it, so we don't
               want the bottom nav / sidebar chrome rendering on the
               printable page. Auth still required. */}
          <Route
            path="/orders/:supplierOrderId/packing-slip"
            element={<RequireSupplier><PackingSlip /></RequireSupplier>}
          />
          <Route path="/" element={<RequireSupplier><Shell /></RequireSupplier>}>
            <Route index              element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard"  element={<DashboardPage />} />
            <Route path="inventory"  element={<InventoryPage />} />
            <Route path="orders"     element={<OrdersPage />} />
            <Route path="service-requests"       element={<ServiceRequestsPage />} />
            <Route path="service-requests/:taskId" element={<ServiceRequestsPage />} />
            <Route path="payouts"            element={<PayoutsPage />} />
            <Route path="payouts/:id/receipt" element={<PayoutReceiptPage />} />
            <Route path="payouts/:id/tax-invoice" element={<TaxInvoicePage />} />
            <Route path="agreements" element={<AgreementsPage />} />
            <Route path="account"    element={<AccountPage />} />
          </Route>
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  )
}
